import { useEffect, useState } from 'react'
import { deleteSignature, getOutreachStyle, listSignatures, saveSignature, type SignatureView } from '../../lib/adminClient'
import { useAuth } from '../../lib/auth'
import { Card, Pill, inputCls } from './ui'

// Signatures for personal emails (to prospects, and the Today list's notes),
// and the messaging guide every message is checked against.

export function Settings(): JSX.Element {
  const session = useAuth()
  const canWrite = session?.role === 'owner' || session?.role === 'admin'
  const [sigs, setSigs] = useState<SignatureView[] | null>(null)
  const [guide, setGuide] = useState<Record<string, { title: string; rules: string[] }> | null>(null)
  const [editing, setEditing] = useState<{ id?: string; name: string; body: string; isDefault: boolean } | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    void listSignatures().then((r) => (r.ok ? setSigs(r.data.signatures) : setErr(r.error)))
    void getOutreachStyle().then((r) => r.ok && setGuide(r.data.guide))
  }, [])

  async function save(): Promise<void> {
    if (!editing) return
    setErr(null)
    const r = await saveSignature(editing)
    if (!r.ok) return setErr(r.error)
    setSigs(r.data.signatures)
    setEditing(null)
  }
  async function remove(id: string): Promise<void> {
    const r = await deleteSignature(id)
    if (r.ok) setSigs(r.data.signatures)
  }

  return (
    <div className="grid lg:grid-cols-2 gap-4 items-start">
      <Card
        title="Email signatures"
        testId="signatures"
        aside={
          canWrite && !editing ? (
            <button className="text-xs text-accent hover:underline" onClick={() => setEditing({ name: '', body: '', isDefault: (sigs ?? []).length === 0 })} data-testid="signature-new">
              Add a signature
            </button>
          ) : null
        }
      >
        <div className="text-xs text-slate-400 mb-3">
          The default is added to every personal email: to prospects, and the notes on the Today list. Keep it short. Your name, what you do, and one link at most reads as a person.
        </div>
        {sigs === null ? (
          <div className="text-sm text-slate-400">Loading…</div>
        ) : sigs.length === 0 && !editing ? (
          <div className="text-sm text-amber-200" data-testid="signatures-empty">No signature yet. Emails go out signed only with the company footer until you add one.</div>
        ) : (
          <ul className="space-y-3">
            {sigs.map((s) => (
              <li key={s.id} className="rounded-lg bg-white/[0.03] p-3" data-testid={`signature-${s.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">
                    {s.name} {s.isDefault && <Pill tone="accent">Default</Pill>}
                  </span>
                  {canWrite && (
                    <span className="flex gap-3 text-xs">
                      {!s.isDefault && (
                        <button className="text-accent hover:underline" onClick={() => void saveSignature({ id: s.id, name: s.name, body: s.body, isDefault: true }).then((r) => r.ok && setSigs(r.data.signatures))}>
                          Make default
                        </button>
                      )}
                      <button className="text-slate-400 hover:text-white" onClick={() => setEditing({ id: s.id, name: s.name, body: s.body, isDefault: s.isDefault })}>
                        Edit
                      </button>
                      <button className="text-slate-500 hover:text-red-300" onClick={() => void remove(s.id)}>
                        Delete
                      </button>
                    </span>
                  )}
                </div>
                <pre className="text-sm text-slate-300 mt-1 whitespace-pre-wrap font-sans">{s.body}</pre>
              </li>
            ))}
          </ul>
        )}
        {editing && (
          <div className="rounded-lg bg-white/[0.03] p-3 mt-3 space-y-2" data-testid="signature-form">
            <input className={inputCls} placeholder="Name it, e.g. Michael, founder" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="signature-name" />
            <textarea className={inputCls} rows={4} placeholder={'Michael\nFounder, Plexii\nplexiidesk.com'} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="signature-body" />
            <label className="text-xs text-slate-400 flex items-center gap-2">
              <input type="checkbox" checked={editing.isDefault} onChange={(e) => setEditing({ ...editing, isDefault: e.target.checked })} /> Use as the default
            </label>
            <div className="flex gap-2">
              <button className="btn-primary" onClick={() => void save()} data-testid="signature-save">
                Save
              </button>
              <button className="btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {err && <div className="text-xs text-red-300 mt-2">{err}</div>}
      </Card>

      <Card title="How messages should read" testId="guide">
        <div className="text-xs text-slate-400 mb-3">Every message on this page is checked against these as you type. A check that finds nothing is necessary, not enough: read it once as the person receiving it would.</div>
        {guide ? (
          <div className="space-y-4">
            {Object.entries(guide).map(([k, g]) => (
              <div key={k}>
                <div className="text-sm font-medium">{g.title}</div>
                <ul className="list-disc pl-5 mt-1 space-y-0.5 text-sm text-slate-300">
                  {g.rules.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-400">Loading…</div>
        )}
      </Card>
    </div>
  )
}
